// A truncated image still decodes: the browser renders the rows it has, grey-fills
// the rest, and an encoder then writes a complete, valid file. That is why
// compressing an upload hides truncation from the server's renderability gate
// (ImageProcessors::RenderabilityCheck) - by the time Rails sees the bytes there is
// nothing left to detect.
//
// So this does not judge whether an image is good; it only answers "was the file
// cut short", which is the one thing compression would erase. Anything it cannot
// judge is reported complete and left to the server, which owns the real decision,
// the customer-facing message and the telemetry.

// Enough to clear any trailing padding after the end marker without reading the
// whole file - the marker sits at the very end of a well-formed file.
const TAIL_BYTES = 512;

// JPEG ends with EOI. Within entropy-coded data a literal 0xFF is escaped as
// FF 00, so FF D9 cannot occur by accident before the real marker.
const JPEG_EOI = [0xff, 0xd9];
// PNG ends with the IEND chunk: its type is followed only by a 4-byte CRC.
const PNG_IEND = [0x49, 0x45, 0x4e, 0x44];

const markerFor = (type) => {
  if (type === 'image/jpeg') return JPEG_EOI;
  if (type === 'image/png') return PNG_IEND;

  return null;
};

// Scan backwards: trailing bytes after the end marker are legal, so the marker
// does not have to be the last thing in the file.
const containsMarker = (bytes, marker) => {
  for (let i = bytes.length - marker.length; i >= 0; i--) {
    if (marker.every((byte, offset) => bytes[i + offset] === byte)) {
      return true;
    }
  }

  return false;
};

// Blob.arrayBuffer is missing on Safari < 14 and the older iOS versions this
// uploader still supports (and in jsdom), so fall back to FileReader there.
const readTail = (file) => {
  const blob = file.slice(-TAIL_BYTES);

  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * @param {File|Blob} file
 * @returns {Promise<boolean>} true only when the format is one we can check and
 *   its end-of-image marker is missing.
 */
export async function isIncompleteImage(file) {
  const marker = markerFor(file?.type);
  if (!marker) {
    return false;
  }

  try {
    const tail = await readTail(file);

    return !containsMarker(new Uint8Array(tail), marker);
  } catch {
    // An unreadable slice is not evidence of truncation, and this must never be
    // the reason an upload is refused.
    return false;
  }
}

export default isIncompleteImage;
